// src/lib/reembedBook.js
//
// Ticket 66. A reader's words reach a book through set_reader_description()
// (the "If You Like What I Like" pitch, and the admin approval queue) and an
// admin un-claim puts the book's text back (revert_claimed_book()). Each of
// those changes the text a book is matched on without re-embedding it, so the
// book's stored embedding no longer matches its text, is_recommendable goes
// false, and the author's My Books shows "Readers can't find this book yet"
// for a book nothing is wrong with.
//
// requestBookReembed() is the follow-up call every one of those writers makes
// straight after it succeeds. It asks batch-reembed-books to embed that one
// book, in its single-book mode, which:
//   - embeds only the text already stored on the book (nothing is sent from
//     here but the id),
//   - does nothing, and costs nothing, if the book is already up to date,
//   - is rate limited in the database.
//
// IT NEVER THROWS, and it never turns into the caller's save failing. The
// save it follows has already succeeded by the time this runs; a failed
// re-embed is a separate, smaller problem (the book stays "saved but not
// searchable" until the author's Retry or the next batch run), so the caller
// gets a result to report, not an exception that could be mistaken for the
// save having failed. A caller that ignores the result still gets a
// console.error here -- npm run check:reembed-on-reader-words fails the build
// if a writer stops calling this at all.
//
// Result: { ok: true,  result: 'embedded' | 'skipped', reason? }
//      or { ok: false, reason: string, limited: boolean }

import { supabase } from './supabase';

const FUNCTION_NAME = 'batch-reembed-books';

// The function's own embedding call waits up to 10s (getEmbedding), plus the
// database reads and the write either side. The outer deadline sits over that
// so a hung call ends in a reported failure instead of a spinner that never
// stops. Needs supabase-js's invoke `timeout` option -- see the note in
// useVoiceRecorder.js about the version that provides it.
const INVOKE_TIMEOUT_MS = 20_000;

export async function requestBookReembed(bookId) {
  const id = Number(bookId);
  if (!Number.isSafeInteger(id) || id <= 0) {
    return { ok: false, limited: false, reason: 'no book id to re-embed' };
  }

  try {
    const { data, error: invokeError } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: { bookId: id },
      timeout: INVOKE_TIMEOUT_MS,
    });

    if (invokeError) {
      // Any non-2xx arrives as invokeError with data null; the real reason is
      // in the response body it carries (same recovery as voiceConsent.js).
      let reason = invokeError.message;
      let limited = false;
      try {
        const body = await invokeError.context?.json?.();
        // Failures answer { ok: false, reason }, but the function's catch-all answers
        // { ok: false, error }; without this a crash showed only "non-2xx".
        const said = body?.reason || body?.error;
        if (said) reason = said;
        limited = body?.limited === true;
      } catch {
        // body wasn't JSON or was already consumed -- keep invokeError.message.
      }
      console.error(`[reembedBook] book ${id} not re-embedded:`, reason);
      return { ok: false, limited, reason };
    }

    if (!data?.ok) {
      const reason = data?.reason || data?.error || 'unexpected response';
      console.error(`[reembedBook] book ${id} not re-embedded:`, reason);
      return { ok: false, limited: data?.limited === true, reason };
    }

    return { ok: true, result: data.result, reason: data.reason };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    console.error(`[reembedBook] book ${id} not re-embedded:`, reason);
    return { ok: false, limited: false, reason };
  }
}
